import React, { useEffect, useRef, useState } from "react";
import { Section, Team as TeamType, User } from "../../types";
import { TeamEdit } from "./Edit";
import { TeamEmpty } from "./Empty";
import { useMutation } from "@apollo/client";
import { DELETE_GROUP, SET_GROUP_LEADER } from "../../Queries";
import { t } from "i18next";
import { useErrorBoundary } from "../../Contexts/ErrorContext";
import { Typography } from "@mui/material";

function filterAndSortTeams(
  teams: TeamType[] | null,
  selectedSection?: Section,
): TeamType[] {
  if (!teams || teams.length === 0) return [];

  let filteredTeams = [...teams];

  // If a section is selected, check if teams need to be filtered out
  if (selectedSection) {
    filteredTeams = teams.filter((team) => {
      // If the team is empty, display it
      if (team.userIds.length === 0) {
        return true;
      }

      // If team contains at least one student from section, show team; otherwise hide it
      return team.userIds.some((userId) =>
        selectedSection.userIds.includes(userId),
      );
    });
  }

  // Group teams with no users
  // const floated = [...teams].sort((x, y) => {
  //   if (x.userIds.length === 0) {
  //     return -1;
  //   }
  //   if (y.userIds.length === 0) {
  //     return 1;
  //   }
  //   return 0;
  // });

  // Sort teams alphabetically by name
  return filteredTeams.sort((a, b) =>
    a?.name.localeCompare(b?.name, undefined, { numeric: true }),
  );
}

interface TeamListProps {
  teams?: TeamType[] | null;
  sectionStudents?: User[] | null;
  allStudents?: User[] | null;
  selectedSection?: Section;
  isLoading: boolean;
  isCreating: boolean;
  onNew: () => void;
  onAssign: (userIds: string[], team: TeamType) => void;
  onChange: (team: TeamType) => void;
}

export const TeamList = ({
  teams = [],
  sectionStudents = [],
  allStudents = [],
  selectedSection,
  isLoading,
  isCreating = false,
  onNew,
  onAssign,
  onChange,
}: TeamListProps) => {
  const { setError } = useErrorBoundary();
  const [editingTeamId, setEditingTeamId] = useState<string | null>(null);
  const teamRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const previousSortIndex = useRef<Map<string, number>>(new Map());

  //
  // Mutations
  //

  const [deleteGroup] = useMutation(DELETE_GROUP, {
    refetchQueries: ["GetGroups"],
    onError: (error) => setError(t("errors.groups.delete"), error),
  });

  const [setTeamLeader] = useMutation(SET_GROUP_LEADER, {
    // refetchQueries: [""]
    onError: (error) => setError(t("errors.groups.setLeader"), error),
  });

  //
  // Event handlers
  //

  function handleTeamDelete(team: TeamType) {
    deleteGroup({
      variables: team,
    });
  }

  function handleSetLeader(user: string, team: TeamType) {
    setTeamLeader({
      variables: {
        id: team.id,
        leaderId: user,
      },
    });
  }

  function handleChange(team: TeamType) {
    onChange(team);
  }

  function handleEditingChange(teamId: string | null) {
    setEditingTeamId(teamId);
  }

  // Track sort index changes and scroll to edited team if position changed
  const sortedTeams = filterAndSortTeams(teams, selectedSection);
  useEffect(() => {
    if (editingTeamId) {
      const currentIndex = sortedTeams.findIndex((t) => t.id === editingTeamId);
      const prevIndex = previousSortIndex.current.get(editingTeamId);

      if (
        currentIndex !== -1 &&
        prevIndex !== undefined &&
        currentIndex !== prevIndex
      ) {
        const teamElement = teamRefs.current.get(editingTeamId);
        if (teamElement) {
          teamElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          setEditingTeamId(null);
        }
      }

      // Update the previous index for this team
      previousSortIndex.current.set(editingTeamId, currentIndex);
    }
  }, [sortedTeams, editingTeamId]);

  // Loading message
  if (isLoading || teams === undefined) {
    return <Typography>Teams loading!</Typography>;
  }

  // If there are no teams to display and the user is not creating a
  // new team, show empty message
  if ((!teams || teams.length === 0) && !isCreating) {
    return <TeamEmpty onAddTeamClick={onNew} />;
  }

  return (
    <>
      {sortedTeams.map((team, index) => {
        // Store the initial sort index
        if (!previousSortIndex.current.has(team.id)) {
          previousSortIndex.current.set(team.id, index);
        }

        return (
          <div
            key={team.id}
            ref={(el) => {
              if (el) {
                teamRefs.current.set(team.id, el);
              } else {
                teamRefs.current.delete(team.id);
              }
            }}
          >
            <TeamEdit
              team={team}
              users={allStudents ? allStudents : []}
              onAssign={onAssign}
              onChange={handleChange}
              onDelete={handleTeamDelete}
              onSetLeader={handleSetLeader}
              onEditingChange={handleEditingChange}
            />
          </div>
        );
      })}
    </>
  );
};
